// src/services/transactionFetcher/transactionFormatUtils.js

import { TRANSACTION_TYPES } from '../../contexts/TransactionContext';

/**
 * Standardize transaction format to ensure consistency across different sources
 * @param {Array} transactions - Array of transaction objects
 * @returns {Array} - Standardized transaction objects
 */
export const standardizeTransactionFormat = (transactions) => {
  if (!Array.isArray(transactions)) {
    console.error('Expected transactions to be an array');
    return [];
  }
  
  return transactions.map(tx => {
    // Ensure all required fields are present
    return {
      hash: tx.hash || '',
      type: Object.values(TRANSACTION_TYPES).includes(tx.type) ? tx.type : TRANSACTION_TYPES.UNKNOWN,
      from: tx.from || '',
      to: tx.to || '',
      value: tx.value || '0',
      timestamp: tx.timestamp || Date.now(),
      status: ['success', 'failed', 'pending'].includes(tx.status) ? tx.status : 'pending',
      tokenSymbol: tx.tokenSymbol || 'UNKNOWN',
      tokenDecimal: tx.tokenDecimal || 18,
      fee: tx.fee || '0',
      network: tx.network || 'unknown',
      data: tx.data || '',
      methodName: tx.methodName || '',
      explorerUrl: tx.explorerUrl || ''
    };
  });
};

/**
 * Merge new transactions with existing ones, avoiding duplicates
 * @param {Array} existingTxs - Existing transactions from localStorage or state
 * @param {Array} newTxs - New transactions from API
 * @returns {Array} - Merged transaction array
 */
export const mergeTransactions = (existingTxs, newTxs) => {
  if (!Array.isArray(existingTxs) || !Array.isArray(newTxs)) {
    return Array.isArray(existingTxs) ? existingTxs : Array.isArray(newTxs) ? newTxs : [];
  }
  
  // Create a map of existing transactions by hash
  const existingTxMap = new Map();
  existingTxs.forEach(tx => {
    existingTxMap.set(tx.hash, tx);
  });
  
  // Add or update transactions from new array
  newTxs.forEach(newTx => {
    // Check if the transaction already exists
    if (existingTxMap.has(newTx.hash)) {
      const existingTx = existingTxMap.get(newTx.hash);
      
      // Only update if the new transaction has more information
      if (newTx.status !== 'pending' || existingTx.status === 'pending') {
        existingTxMap.set(newTx.hash, {
          ...existingTx,
          ...newTx,
          // Keep the original timestamp if it exists
          timestamp: existingTx.timestamp || newTx.timestamp
        });
      }
    } else {
      existingTxMap.set(newTx.hash, newTx);
    }
  });
  
  // Convert map back to array and sort by timestamp (newest first)
  return Array.from(existingTxMap.values())
    .sort((a, b) => b.timestamp - a.timestamp);
};

/**
 * Helper to determine if a transaction involves a specific address
 * @param {Object} tx - Transaction object
 * @param {string} address - Address to check
 * @returns {boolean} - True if the transaction involves the address
 */
export const transactionInvolvesAddress = (tx, address) => {
  if (!tx || !address) return false;
  
  const normalizedAddress = address.toLowerCase();
  const from = (tx.from || '').toLowerCase();
  const to = (tx.to || '').toLowerCase();
  
  return from === normalizedAddress || to === normalizedAddress;
};

/**
 * Helper to filter transactions by network
 * @param {Array} transactions - Array of transaction objects
 * @param {string} network - Network key to filter by
 * @returns {Array} - Filtered transaction array
 */
export const filterTransactionsByNetwork = (transactions, network) => {
  if (!Array.isArray(transactions) || !network) return [];
  
  return transactions.filter(tx => tx.network === network);
};